import { AnswerExplanationPosition } from "./components/answer-explanation"
import { StudentStats } from "models/reports-model"
import { hasText } from "utils/helpers"
import BroadcastMessage, {
  ConnectScreen,
  DragDropCategory,
  DragDropScreen,
  DrawScreen,
  KeyboardScreen,
  LessonScreen,
  MapScreen,
  OpenTextScreen,
  PlaySessionCertificate,
  PollAnswer,
  PollScreen,
  PresentationScreen,
  QuizAnswersLayouts,
  QuizScreen,
  ReportedAnswer,
  ReportIndex,
  ScreenType,
  Theme,
} from "models/tables"
import StudentClassResponse, { LoggedInUser, Page } from "models/dto"

export type LoadingReason =
  | "ClassroomLobby"
  | "OtherStudentsAnswering"
  | "LoadingImages"
  | "Loading"
  | "TeacherWaitsForStudentFeedback"

export type FrameType =
  | "GameCode"
  | "UserName"
  | "PageNotFound"
  | "ShowMeme"
  | "StudentLeaderboard"
  | "Lobby"
  | "Loading"
  | "FinalScreen"

export type NewClassroomGameOptions = NewClassroomGameRequest & {
  lessonId: string
}

export interface NewClassroomGameRequest {
  classId: string | null
  disableMemes: boolean
  enableTimer: boolean
  certificateMinSuccessRate?: number
  enableGraphicalLogin: boolean
  shuffleAnswers: boolean
  shuffleScreens: boolean
}

export interface NewClassroomGameResponse {
  connectionId: string
  gameCode: string
  uniqueUrls: string[]
  students: ClientStudent[]
  lessonName: string
  lessonImage?: string | null
  className: string
  timerEnabledForSession: boolean
}

export interface ClientStudent {
  connectionId: string
  userId: string | null
  fullName: string
  hasJoined: boolean
  avatarId: number
}

export interface StartedResponse extends NextTimerState {
  screen: ClientScreen
  imagesToPreload: string[]
  connectionId: string
  reportId: string | null
  countStartedStudents: number
  countScreens: number
}

export interface StudentAnsweredResponse {
  studentsCount: number
}

export interface ReviewData {
  originalScreen?: LessonScreen
  answer?: ReportedAnswer
  connectItems: ConnectFinishedItem[]
}

export type ReviewScreenResponse = NextTimerState & {
  screen: ClientScreen
  originalScreen?: LessonScreen
  reportedAnswer?: ReportedAnswer
  connectItems: ConnectFinishedItem[]
  screenIndex: number
  canReviewPrevScreen: boolean
  canReviewNextScreen: boolean
}

export interface JoinResponse extends NextTimerState {
  screen?: ClientScreen
  reportId?: string
  uniqueUrls: string[] | undefined
  connectionId: string
  fullName: string
  userId: string
  error: JoinRequestError
  sessionMode: SessionMode
  className: string
  disableMemes: boolean
  canReviewPrevScreen: boolean
  canReviewNextScreen: boolean
  countScreens: number
  waitForOtherStudents: boolean
  isBusinessVersion: boolean
  newGameLink?: string
  newGameText?: string
  theme?: Theme | null
}

export interface FinishScreenOptions {
  timeout: boolean
}

export type PendingMessageError =
  | "PendingMessagesNotSupported"
  | "GameNotFound"
  | "ParticipantNotFound"

export type PendingMessagesResponse = {
  error?: PendingMessageError | null
  messages: BroadcastMessage[]
}

export type ServerError =
  | "Generic"
  | "ConnectionLost"
  | "FailedToStartClassroomGame"
  | "ReportNotFound"
  | "NotLoggedIn"
  | null

/** What the player was doing when a server request failed */
export type PlayErrorAction =
  | "Answer"
  | "NextScreen"
  | "ReviewNext"
  | "ReviewPrevious"
  | "ToggleTimer"
  | "TimerTimeout"
  | "StartGame"
  | "RemoveStudent"
  | "Messages"

/**
 * - `action`: one thing the player did has failed; the game goes on and they can try again
 * - `behind`: the server refused the request because it has it already (a
 *   second tab, a response that got lost): this page shows a screen the
 *   player has passed, and trying again cannot help, reloading does
 * - `fatal`: the game cannot go on without reloading the page
 */
export type PlayErrorLevel = "action" | "behind" | "fatal"

/**
 * A failed server request, as shown to the player (PlayErrorNotice) and
 * reported to the server (error-report.ts). The player sees a friendly text
 * and `code`; the rest is what the report carries.
 */
export interface PlayError {
  /** Short random code shown to the player; what a screenshot is looked up by */
  code: string
  level: PlayErrorLevel
  action: PlayErrorAction
  /** ISO time of the failure, by this device's clock */
  time: string
  /** `time` as shown next to the code: HH:mm:ss, local */
  shownTime: string
  method: string | null
  url: string | null
  /** 0 when no response arrived */
  statusCode: number
  /** The failed response's X-Request-Id */
  requestId: string | null
  message: string | null
  responseBody: string | null
}

export interface PreviewScreen {
  screen: LessonScreen
  countScreens: number
}

export enum JoinRequestError {
  none,
  usernameTaken,
  requireLogin,
  studentNotInClass,
  alreadyPlayed,
  gameCodeMissing,
  gameStarted,
  requireUsername,
  usernameTooShort,
  gameCodeExpired,
  gameCodeInactive,
  requireGraphicalLogin,
  tooManyIncomingRequests,
  lessonHasNoScreens,
}

export type ScreenFinishedFeedback = "None" | "Leaderboard" | "Meme"

export interface ClientConnectItem {
  id: string
  text?: string
  url?: string
  width?: number
  height?: number
}

export interface ClientScreen {
  id: string
  questionImage: string
  questionImageWidth: number
  questionImageHeight: number
  quizAnswersLayout: QuizAnswersLayouts
  questionAudio: string
  questionVideo: string
  questionVideoStart: number
  questionVideoEnd: number
  questionText: string
  secondsToComplete: number
  quizAnswers: QuizAnswer[]
  pollAnswers: PollAnswer[]
  mapAnswers: MapAnswer[]
  connectLefts: ClientConnectItem[]
  connectRights: ClientConnectItem[]
  dragDropCategories: DragDropCategory[]
  alphabet: string
  currentScreen: number
  screenType: ScreenType
  answerExplanation: string
}

export function lessonScreenToClientScreen(screen: LessonScreen): ClientScreen {
  const result: Partial<ClientScreen> = {
    currentScreen: 1,
  }

  result.screenType = screen.type

  switch (screen.type) {
    case ScreenType.Presentation: {
      const src = screen as PresentationScreen
      result.questionText = src.question
      result.questionAudio = src.questionAudioUrl
      result.questionImage = src.questionImageUrl
      result.questionVideo = src.youtubeUrl
      result.questionVideoStart = src.youtubeStart
      result.questionVideoEnd = src.youtubeEnd
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      break
    }

    case ScreenType.Connect: {
      const src = screen as ConnectScreen
      result.questionText = src.question
      result.connectLefts = []
      result.connectRights = []
      for (let p of src.pairs) {
        result.connectLefts.push({
          id: p.id,
          url: p.leftImageUrl,
          text: p.leftText,
          width: p.leftImageWidth || 0,
          height: p.leftImageHeight || 0,
        })
        result.connectRights.push({
          id: p.id,
          url: p.rightImageUrl,
          text: p.rightText,
          width: p.rightImageWidth || 0,
          height: p.rightImageHeight || 0,
        })
      }
      result.answerExplanation = src.answerExplanation
      break
    }

    case ScreenType.DragDrop: {
      const src = screen as DragDropScreen
      result.dragDropCategories = src.categories
      result.answerExplanation = src.answerExplanation
      break
    }

    case ScreenType.Drawing: {
      const src = screen as DrawScreen
      result.questionText = src.question
      result.questionImage = src.questionImageUrl
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      break
    }

    case ScreenType.Keyboard: {
      const src = screen as KeyboardScreen
      result.questionText = src.question
      result.questionImage = src.questionImageUrl
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      result.alphabet = src.alphabet
      result.answerExplanation = src.answerExplanation
      break
    }

    case ScreenType.OpenText: {
      const src = screen as OpenTextScreen
      result.questionText = src.question
      result.questionImage = src.questionImageUrl
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      result.answerExplanation = src.answerExplanation
      break
    }
    case ScreenType.Map: {
      const src = screen as MapScreen
      result.questionImage = src.questionImageUrl
      result.questionText = src.question
      result.mapAnswers = src.ellipseAnswers.map((x) => ({
        id: x.id || "",
        x: x.x,
        y: x.y,
        width: x.width,
        height: x.height,
      }))
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      break
    }

    case ScreenType.Quiz: {
      const src = screen as QuizScreen
      result.questionImage = src.questionImageUrl
      result.questionText = src.question
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      result.quizAnswers = src.answers
        .map((x) => ({
          id: x.id || "",
          isCorrect: x.isCorrect,
          text: x.text,
          url: x.url,
        }))
        .filter(
          (x) =>
            (typeof x.url === "string" && x.url.length > 0) || hasText(x.text)
        )
      break
    }
    case ScreenType.Poll: {
      const src = screen as PollScreen
      result.questionImage = src.questionImageUrl
      result.questionText = src.question
      result.questionImageWidth = src.questionImageWidth
      result.questionImageHeight = src.questionImageHeight
      result.answerExplanation = src.answerExplanation
      result.pollAnswers = src.answers.filter(
        (x) =>
          (typeof x.url === "string" && x.url.length > 0) || hasText(x.text)
      )
      break
    }

    default: {
      throw "not implemented screen type"
    }
  }

  return result as ClientScreen
}

export interface DragDropFinalState {
  byCategory: {
    categoryId: string
    answersIds: string[]
  }[]
  unansweredIds: string[]
}

export interface DragDropAnswerRequest
  extends AnswerRequest,
    DragDropFinalState {}

export interface NextScreenRequest extends AnswerRequest {}

export interface ConnectFinalStatePair {
  leftId: string
  rightId: string
}

export interface ConnectFinalState {
  pairs: ConnectFinalStatePair[]
}

export interface ConnectAnswerRequest
  extends ConnectFinalState,
    AnswerRequest {}

export interface StudentAddedResponse {
  totalStudents: number
  student: ClientStudent
}

export interface ReconnectResponse extends NextTimerState {
  screen: ClientScreen | null
  points: number
  fullName: string
  avatarId: string
  newSocketId: string
  gameCode: string
  mode: SessionMode
  disableMemes: boolean
  isTeacher: boolean
  isReview: boolean
  canReviewPrevScreen: boolean
  canReviewNextScreen: boolean
  countScreens: number
  originalScreen?: LessonScreen
  reportedAnswer?: ReportedAnswer
  connectItems: ConnectFinishedItem[]
  gameStarted?: boolean
  /** The student has answered the screen they are on (a classroom waits for the teacher) */
  screenAnswered?: boolean
  classId?: string
  className?: string
  isBusinessVersion: boolean
  theme?: Theme | null
}

export interface NextTimerState {
  timerTotalSeconds: number
  timerSecondsRemaining?: number
  timerVisible: boolean
  timerRunning: boolean
  timerEnabledForSession: boolean
}

export interface ReconnectTeacherResponse
  extends ClassroomGameStartedTeacherResponse {
  gameCode: string
  newConnectionId: string
  currentScreenIndex: number
  lessonName: string
  lessonImage?: string | null
  classId?: string | null
  className?: string | null
  lobby: ClientStudent[]
}

export interface ReconnectSessionResponse {
  result: ReconnectResponse | null
  success: boolean
}

export type Answer = QuizAnswer | MapAnswer

export interface QuizAnswer {
  id: string
  isCorrect: boolean
  text: string
  url?: string
}

export interface PlayPollAnswer extends PollAnswer {
  isSelected: boolean
}

export interface AnswerRequest {
  isTimeout: boolean
  screenId: string
}

export interface QuizAnswerRequest extends AnswerRequest {
  answerIds: string[]
}

export interface PollAnswerRequest extends AnswerRequest {
  answerIds: string[]
}

export interface MapAnswer {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export interface MapAnswerRequest extends AnswerRequest {
  x: number
  y: number
}

export interface LeaderboardStudent {
  id: string
  fullName: string
  avatarId: string
  points: number
}

export type GameError =
  | "None"
  | "ClassroomGameNotStarted"
  | "StudentCannotAdvanceInClassroom"
  | "NotAuthorized"
  | "InvalidScreen"

export interface ScreenFinishedResponse extends NextTimerState {
  error: GameError
  reportId?: string
  isBroadcast?: boolean
  screen: ClientScreen | null | undefined
  correctAnswer: string
  totalPoints: number
  lastScreenPoints: number
  lastScreenCorrect: boolean
  leaderboard: LeaderboardStudent[]
  accuracy: number
  lastScreenType?: ScreenType
  memeUrl: string
  mode: SessionMode
  lastCorrectAnswerForQuiz: string[]
  connectItems: ConnectFinishedItem[]
  feedback: ScreenFinishedFeedback
  canReviewPrevScreen: boolean
  canReviewNextScreen: boolean
  isReview: boolean
  showFeedbackForMs: number
  showCorrectForMs: number
  newGameMessage?: string
  newGameLink?: string
}

export interface ConnectFinishedItem {
  leftId: string
  rightId: string
  isCorrect: boolean
}

export interface ClassroomGameStartedTeacherResponse extends NextTimerState {
  screen?: ClientScreen
  reportId: string
  students: ClientStudent[]
  memesAreDisabled: boolean
  countScreens: number
  enableGraphicalLogin?: boolean
}

export enum SessionMode {
  Classroom = 0,
  Homework = 1,
}

export interface PlacePinPayload {
  normalizedPin: MapPin
}

export interface SelectPairPayload {
  type: "left" | "right"
  id: string
}

export interface StartDraggingFromAnswersPayload {
  answerId: string
  zoneIndex: number
}

export interface StartDraggingFromZonePayload {
  answerId: string
  zoneId: string
  targetIdx: number
}

export interface DropZonePayload {
  answerId: string
  targetId: string
}

export interface MapPin {
  x: number
  y: number
}

export interface PlayQuizAnswer extends QuizAnswer {
  isSelected: boolean
  ordinal: number
}

export type ImageType = "w" | "h"

export interface PlayConnectItem {
  id: string
  text?: string
  url?: string
  type: ImageType
  width?: number
  height?: number
  connectedToIdx: number
  colorIndex: number
  isSelected: boolean
  isCorrect: boolean | null
}

export interface PlayConnectPair {
  left: PlayConnectItem
  right: PlayConnectItem
}

export interface PlayDragDropAnswer {
  id: string
  categoryId: string
  dropTargetIndex: number
  text: string
  imageType: ImageType
  imageUrl: string
  ordinal: number
  /** set only in review, when the server determines if this is correctly placed */
  isCorrect?: boolean
}

export interface PlayAnswerPile {
  isDraggedFrom: boolean
  answers: PlayDragDropAnswer[]
}

export interface PlayDropZone {
  categoryId: string
  title: string
  targets: PlayAnswerPile[]
  ordinal: number
  isDraggedOver: boolean
}

export function connectItemType(w: number, h: number): ImageType {
  return w >= h ? "w" : "h"
}

export const maxCountConnectPairs = 4

export function invalidMapPin(): MapPin {
  return { x: -1, y: -1 }
}

export interface PlayConnectState {
  pairs: PlayConnectPair[]
  remainingColors: number[]
  connection: {
    left: number
    right: number
  }
}

export function emptyConnect(): PlayConnectState {
  return {
    pairs: [],
    remainingColors: [0, 1, 2, 3],
    connection: {
      left: -1,
      right: -1,
    },
  }
}

export interface PlayDrawingState {
  drawingSeries: DrawingSeries[]
}

export const DEFAULT_DRAW_IMAGE = "/img/draw_big_background.png?v=2"

export function emptyDrawing(): PlayDrawingState {
  return {
    drawingSeries: [],
  }
}

export function emptyHistoryPage(): PlayHistoryState {
  return {
    pageIndex: null,
    page: {
      totalCountPages: 0,
      totalItemsCount: 0,
      items: [],
      pageSize: 0,
    },
    stats: [],
    loadingReportId: null,
  }
}

export interface PlayKeyboardState {
  answer: string
}

export interface PlayOpenTextState {
  answer: string
}

export interface OpenTextAnswerRequest extends AnswerRequest {
  answerText: string
}

export interface KeyboardAnswerRequest extends AnswerRequest {
  answerText: string
}

export function emptyKeyboard(): PlayKeyboardState {
  return {
    answer: "",
  }
}

export function emptyOpenText(): PlayOpenTextState {
  return {
    answer: "",
  }
}

export interface PlayMapState {
  normalizedPin: MapPin | null
}

export function emptyMap(): PlayMapState {
  return {
    normalizedPin: null,
  }
}

export function emptyDragDrop(): PlayDragDropState {
  return {
    dropZones: [],
    answerPiles: [],
  }
}

export interface PlayHistoryReport {
  report: ReportIndex
  stats: StudentStats
}

export interface PlayHistoryState {
  pageIndex: number | null
  page: Page<ReportIndex>
  stats: PlayHistoryReport[]
  loadingReportId: string | null
}

export interface ProfilePageValidation {
  name: string | null
  email: string | null
  city: string | null
  profilePicture: string | null
}

export function emptyProfilePageValidation(): ProfilePageValidation {
  return {
    name: null,
    email: null,
    city: null,
    profilePicture: null,
  }
}

export interface ProfilePageState {
  classes: StudentClassResponse[]
  joinError: string | null
  validationErrors: ProfilePageValidation
}

export function emptyProfilePageState(): ProfilePageState {
  return {
    classes: [],
    joinError: null,
    validationErrors: emptyProfilePageValidation(),
  }
}

export interface PlayDragDropState {
  dropZones: PlayDropZone[]
  answerPiles: PlayAnswerPile[]
}

export type Point = {
  x: number
  y: number
}

export type DrawingSeries = {
  color: string
  radius: number
  points: Point[]
}

export interface DrawingSubmitRequest extends AnswerRequest {
  drawingSeries: DrawingSeries[]
}

export type ToggleTimerRequest = {
  enable: boolean
  secondsRemaining: number
  time: Date
}

export type ToggleTimerResponse = {
  isEnabled: boolean
  secondsRemaining: number
}

export type TimerState = {
  visible: boolean
  running: boolean
  totalSeconds: number
  progress: number
  secondsRemaining: number
  timerOffsetMs: number
  initialTimestamp: DOMHighResTimeStamp | null
  enabledForSession: boolean
}

export function emptyTimerState(args?: {
  enabledForSession?: boolean
}): TimerState {
  return {
    visible: false,
    running: false,
    totalSeconds: 0,
    secondsRemaining: 0,
    timerOffsetMs: 0,
    initialTimestamp: null,
    progress: 0,
    enabledForSession: !!args?.enabledForSession,
  }
}

declare global {
  interface Window {
    globalDontPreventBrowserClose?: boolean
  }
}

export function getGlobalDontPreventBrowserClose(): boolean | undefined {
  return window.globalDontPreventBrowserClose
}

export function setGlobalDontPreventBrowserClose(newValue: boolean): void {
  window.globalDontPreventBrowserClose = newValue
}

export type PlayHubState = {
  isServerRequestInProgress: boolean
  startClassroomGameAttemptedCount: number
  studentPageLoaded: boolean
  studentPageLoading: boolean
  teacherPageLoaded: boolean
  teacherPageLoading: boolean
  connectionId: string | null
  oldConnectionId: string | null
  lessonId: string | null
  screenId: string | null
  reportId: string | null
  theme: Theme | null
  classId: string | null
  className: string | null
  lessonName: string | null
  lessonImage: string | null
  isTeacherUrl: boolean
  isPreview: boolean
  teacherConnectionId: string | null
  isStudentRemoved: boolean
  fullName: string | null
  mode: SessionMode | null
  enableGraphicalLogin: boolean
  countAnsweredStudents: number
  countStartedStudents: number
  answerExplanationPosition: AnswerExplanationPosition | undefined
  gameCode: string
  joinError: JoinRequestError | null
  isBusinessVersion: boolean
  loggedUser: LoggedInUser | null
  loggedUserInfoReceived: boolean

  students: ClientStudent[]
  studentsInLobby: ClientStudent[]

  sessionStarted: boolean
  serverError: ServerError
  serverErrorData: unknown

  /** The failure the player is being told about; null when there is none */
  playError: PlayError | null
  /** The message poll is failing and being retried: not an error yet */
  isReconnecting: boolean

  internalPage: FrameType | null
  newGameLink?: string
  newGameMessage?: string
  loadingReason: LoadingReason | null
  screen: ClientScreen | null | undefined
  gameCompleted: boolean
  screenIndex: number
  countScreens: number
  points: number
  lastScreenPoints: number
  lastScreenIsCorrect: boolean
  leaderboard: LeaderboardStudent[]
  feedbackDurationInSeconds: number
  accuracy: number
  memeUrl: string | null
  disableMemes: boolean
  screenFinishedResponse: ScreenFinishedResponse | null
  correctAnswerText: string | null
  correctAnswerIds: string[]

  imagesToPreload: string[]
  imagesPreloaded: boolean

  timer: TimerState
  nextTimer: NextTimerState | null

  canReviewPrevScreen: boolean
  canReviewNextScreen: boolean
  isReview: boolean

  navigationIsLoading: boolean
  nextScreenButtonIsVisible: boolean
  nextScreenButtonIsActive: boolean
  prevScreenButtonIsVisible: boolean
  prevScreenButtonIsActive: boolean

  showCorrectAnswers: boolean
  showAnswerExplanation: boolean

  finished: boolean

  // state computed form ClientScreen
  dragDrop: PlayDragDropState
  quizAnswers: PlayQuizAnswer[]
  pollAnswers: PlayPollAnswer[]
  map: PlayMapState
  keyboard: PlayKeyboardState
  openText: PlayOpenTextState
  connect: PlayConnectState
  drawing: PlayDrawingState
  history: PlayHistoryState
  loginUrl: string | null

  certificates: PlaySessionCertificate[]
  profilePage: ProfilePageState
}
